import { useSuspenseQuery, type MutationFunction } from "@tanstack/react-query";
import { ButtonGroup, Flex, PrimarySecondaryPage, StatusBanner } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { ManagementAccountsUploadSupportingContent } from "../../../components";
import { COMMON_MESSAGES } from "../../../components/common-messages";
import { CONNECT_APP_ROUTE_PATHS } from "../../../components/constants";
import { useAccountingDocumentsValidation } from "../../../hooks/useAccountingDocumentsValidation";
import { getCustomerBusinessModelQueryOptions } from "../../../queries/customer-data/queries";
import {
  useTriggerValidation,
  useValidatedDocumentQuery,
  useVerifyDocuments,
} from "../../../queries/document-uploads/validated-documents-queries";
import {
  useFundingApplication,
  useFundingApplicationMutation,
  type FormattedFundingApplicationScreenUpdate,
} from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";
import { getAccountingThreshold } from "../../../utils/funding-journey";
import { ProfitAndLossDocumentUpload } from "../components";
import { MESSAGES } from "../components/message-definitions";

import { useDocumentValidationMessages } from "./use-document-validation-messages";

export const VerifyProfitAndLossPage = () => {
  const intl = useIntl();
  const { navigate } = useSpaNavigate("FundingApp");
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplicationData } = useFundingApplication(customerDomain);
  const { data: businessModel } = useSuspenseQuery(getCustomerBusinessModelQueryOptions(customerDomain));
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { data: uploadedDocuments } = useValidatedDocumentQuery(customerDomain, fundingApplicationData.id);

  const { mutate: triggerValidation } = useTriggerValidation(
    customerDomain,
    fundingApplicationData.id,
    UploadDocumentType.PROFIT_AND_LOSS
  );
  const { mutate: verifyDocuments, isPending } = useVerifyDocuments(
    customerDomain,
    fundingApplicationData.id,
    UploadDocumentType.PROFIT_AND_LOSS
  );

  const filteredFiles = uploadedDocuments
    ?.filter((doc) => doc.documentType === UploadDocumentType.PROFIT_AND_LOSS)
    .flatMap(({ data }) => data as FundingApplicationSchemas.ValidatedFile[]);
  const newlyUploadedFiles = filteredFiles?.filter((doc) => doc.validationStatus === "ready");

  const validationResults = useAccountingDocumentsValidation({ docs: filteredFiles ?? [] });
  const renderedIssues = useDocumentValidationMessages(validationResults, UploadDocumentType.PROFIT_AND_LOSS);

  const hasUploadedDocuments = Boolean(filteredFiles?.length);

  const onConfirm = () => {
    if (newlyUploadedFiles?.length) {
      triggerValidation(newlyUploadedFiles.map(({ id }) => id));
    }
    if (filteredFiles?.length) {
      verifyDocuments(filteredFiles.map(({ id }) => id));
      navigate(CONNECT_APP_ROUTE_PATHS.SUMMARY);
    }
  };

  const onCancel = () => navigate(CONNECT_APP_ROUTE_PATHS.SUMMARY);

  return (
    <PrimarySecondaryPage
      title={intl.formatMessage({
        id: "funding-application: verify profit and loss",
        defaultMessage: "Verify Profit and Loss Statement",
      })}
      primaryContent={
        <Flex direction="column" gap="6">
          <StatusBanner
            variant="warning"
            title={intl.formatMessage(MESSAGES.validationIssuesInstruction)}
            subheading={renderedIssues}
          />
          <ProfitAndLossDocumentUpload
            fundingApplication={fundingApplicationData}
            accountingThreshold={getAccountingThreshold(businessModel)}
            mutate={fundingApplicationMutation.mutate as MutationFunction<FormattedFundingApplicationScreenUpdate>}
            hasUploadedDocuments={hasUploadedDocuments}
            isVerificationPage
          />
          <ButtonGroup
            primaryAction={{
              content: intl.formatMessage(COMMON_MESSAGES.confirm),
              loading: isPending,
              disabled: !hasUploadedDocuments,
              onAction: onConfirm,
            }}
            secondaryAction={{
              content: intl.formatMessage(COMMON_MESSAGES.backLabel),
              onAction: onCancel,
            }}
          />
        </Flex>
      }
      secondaryContent={<ManagementAccountsUploadSupportingContent />}
    />
  );
};
